import RoomPersonaDetails from './RoomPersonaDetails';
import { roomResultAriaLabel } from './RoomResultsList.locstring.json';
import { CalendarEventTime, Room } from './RoomSelector.types';
import getRoomFreeBusyTextAndStyle from './utils/getRoomFreeBusyTextAndStyle';
import getRoomFreeBusy from '../../utils/getRoomFreebusy';
import getRoomFeaturesAriaLabels from './utils/getRoomFeaturesAriaLabels';
import { observer } from 'mobx-react-lite';
import { Persona, PersonaSize } from '@fluentui/react/lib/Persona';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FreeBusyCacheManager, FreeBusyType } from 'owa-freebusy-cache';
import loc, { format } from 'owa-localize';
import { RoomIcon } from 'owa-room-control';
import React, { useCallback } from 'react';
import { ShimmerElementsGroup, ShimmerElementType, Shimmer } from '@fluentui/react/lib/Shimmer';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import { getPaletteAsRawColors } from 'owa-theme';

export interface RoomResultProps {
    room: Room;
    showOnlyAvailableRooms: boolean;
    freeBusyCacheManager: FreeBusyCacheManager;
    calendarEvent: CalendarEventTime;
    colorScheme: ColorScheme;
    onRoomSelected: () => void;
}

const styles = {
    base: require('./RoomResult.scss'),
    desktop: require('./RoomResult.outlookdesktop.scss'),
    mac: require('./RoomResult.macoutlook.scss'),
};

export default observer(function RoomResult(props: RoomResultProps) {
    const { room, calendarEvent, freeBusyCacheManager, onRoomSelected } = props;
    const { getClass } = useClientStyles(styles);

    const roomAvailability = getRoomFreeBusy(
        room.email,
        freeBusyCacheManager,
        calendarEvent,
        room.bookingType
    );

    const freebusyText = getRoomFreeBusyTextAndStyle(roomAvailability, room.bookingType).text;

    const ariaLabel = format(
        loc(roomResultAriaLabel),
        room.name,
        freebusyText,
        getRoomFeaturesAriaLabels(room)
    );

    const secondaryText = useCallback(
        () => <RoomPersonaDetails room={room} roomAvailability={roomAvailability} />,
        [roomAvailability]
    );
    const roomIcon = useCallback(
        () => (
            <RoomIcon
                colorScheme={props.colorScheme}
                backgroundColor={getPaletteAsRawColors().themePrimary}
                className={getClass('roomIcon')}
            />
        ),
        []
    );
    const shimmerElement = (
        <>
            <div style={{ display: 'flex' }}>
                <ShimmerElementsGroup
                    shimmerElements={[
                        { type: ShimmerElementType.gap, width: 16, height: 32 },
                        { type: ShimmerElementType.circle, height: 32 },
                        { type: ShimmerElementType.gap, width: 16, height: 40 },
                    ]}
                />
                <ShimmerElementsGroup
                    flexWrap={true}
                    width={'100%'}
                    shimmerElements={[
                        {
                            type: ShimmerElementType.line,
                            width: '100%',
                            height: 10,
                            verticalAlign: 'bottom',
                        },
                        { type: ShimmerElementType.line, width: '90%', height: 8 },
                        { type: ShimmerElementType.gap, width: '10%', height: 20 },
                    ]}
                />
                <ShimmerElementsGroup
                    shimmerElements={[{ type: ShimmerElementType.gap, width: 16, height: 40 }]}
                />
            </div>
            <div className={getClass('shimmerGap')} />
        </>
    );

    if (roomAvailability.freeBusyState === FreeBusyType.Loading) {
        return <Shimmer customElementsGroup={shimmerElement} />;
    }

    if (
        isFeatureEnabled('cal-advancedRoomBooking') &&
        props.showOnlyAvailableRooms &&
        roomAvailability.freeBusyState !== FreeBusyType.Free
    ) {
        return null;
    }

    return (
        <div
            className={getClass('roomPersona')}
            data-is-focusable={true}
            role="listitem"
            aria-label={ariaLabel}
            onClick={onRoomSelected}
            tabIndex={-1}
            key={room.email}>
            <Persona
                aria-hidden={true}
                primaryText={room.name}
                size={PersonaSize.size24}
                initialsColor={'transparent'}
                showSecondaryText={true}
                onRenderSecondaryText={secondaryText}
                onRenderPersonaCoin={roomIcon}
                styles={{
                    root: getClass('roomPersonaRoot'),
                    details: { paddingLeft: 12, paddingRight: 0 },
                    secondaryText: getClass('roomPersonaSecondaryText'),
                    primaryText: getClass('roomPersonaPrimaryText'),
                }}
            />
        </div>
    );
});
